import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

import { STRUCTURED_DATA_CONTEXT } from 'src/components/seo/structured-data';
import type { ClubHeaderContent } from 'src/content/cms-provider/club-cms-provider';
import type { GlobalCmsRecord } from 'src/content/cms-provider/global-cms-provider';
import type { BuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import {
  getSiteUrlForPath,
  isClubLinkCurrentPage,
  isGlobalLinkCurrentPage,
} from 'src/helpers/navigation-helper';

export const REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE = 'redesign-breadcrumbs-height';

type BreadcrumbTrail = {
  pageName: string;
  pageUrl?: string | null;
  previousPages: {
    name: string;
    url?: string | null;
  }[];
};

const reduceMenuItemToBreadcrumbTrails = (
  menuItem:
    | Queries.HeaderLevelOneMenuItem
    | Queries.HeaderLevelTwoMenuItem
    | Queries.HeaderLevelThreeMenuItem,
  parentStructure: BreadcrumbTrail
): BreadcrumbTrail[] => {
  const currentPageTrail: BreadcrumbTrail = {
    previousPages: parentStructure
      ? [
          ...parentStructure.previousPages,
          {
            name: parentStructure.pageName,
            url: parentStructure.pageUrl,
          },
        ]
      : [],
    pageName: menuItem.name,
    pageUrl: menuItem.link,
  };

  const childItems =
    (menuItem as Queries.HeaderLevelOneMenuItem).levelTwoMenuItems ||
    (menuItem as Queries.HeaderLevelTwoMenuItem).levelThreeMenuItems ||
    [];

  const childBreadcrumbTrails = childItems.flatMap((childItem) =>
    reduceMenuItemToBreadcrumbTrails(childItem, currentPageTrail)
  );

  return [currentPageTrail, ...childBreadcrumbTrails];
};

const mapHeaderContentToBreadcrumbTrails = (
  headerContent: GlobalCmsRecord['headerContent'] | ClubHeaderContent,
  homePageName: string
): BreadcrumbTrail[] => {
  if (!headerContent) {
    return [];
  }

  const basePageStructure: BreadcrumbTrail = {
    previousPages: [],
    pageName: homePageName,
    pageUrl: '/',
  };

  const headerMenuBreadcrumbTrails = (headerContent.headerMenuItems ?? []).flatMap(
    (levelOneMenuItem) => reduceMenuItemToBreadcrumbTrails(levelOneMenuItem, basePageStructure)
  );

  const secondaryNavBreadcrumbTrails: BreadcrumbTrail[] = (
    headerContent.headerSecondaryUpperMenu ?? []
  ).map((value) => ({
    previousPages: [
      {
        name: homePageName,
        url: '/',
      },
    ],
    pageName: value.text,
    pageUrl: value.link,
  }));

  return [...headerMenuBreadcrumbTrails, ...secondaryNavBreadcrumbTrails];
};

export const calculateCurrentBreadcrumbTrail = (
  homePageName: string,
  override: Queries.BreadcrumbsOverride | null,
  headerContent: GlobalCmsRecord['headerContent'] | ClubHeaderContent,
  language: Language,
  buildType: BuildType,
  basePath: string,
  siteId?: string
): BreadcrumbTrail | null => {
  if (override?.useOverride) {
    return {
      previousPages: (override.previousPages ?? []).map((p) => ({ ...p })),
      pageName: override.currentPageName ?? '',
      pageUrl: '',
    };
  }

  const headerBreadcrumbTrails = mapHeaderContentToBreadcrumbTrails(headerContent, homePageName);

  return (
    headerBreadcrumbTrails.find(
      (bt) =>
        bt.pageUrl &&
        (siteId
          ? isClubLinkCurrentPage(bt.pageUrl, language, siteId, basePath)
          : isGlobalLinkCurrentPage(bt.pageUrl, language, buildType, basePath))
    ) ?? null
  );
};

export const createBreadcrumbsStructuredData = (
  breadcrumbsTrail: BreadcrumbTrail,
  buildType: BuildType
): WithContext<BreadcrumbList> | null => {
  if (!breadcrumbsTrail || breadcrumbsTrail.previousPages.length === 0) {
    return null;
  }

  const previousPagesWithUrl = breadcrumbsTrail.previousPages.filter((p) => !!p.url);

  const breadcrumbList: ListItem[] = [
    ...previousPagesWithUrl.map(
      (page, index) =>
        ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': page.name,
          'item': getSiteUrlForPath(buildType, page.url!),
        }) satisfies ListItem
    ),
    {
      '@type': 'ListItem',
      'position': previousPagesWithUrl.length + 1,
      'name': breadcrumbsTrail.pageName,
    },
  ];

  return {
    '@context': STRUCTURED_DATA_CONTEXT,
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbList,
  };
};
